<template>
  <div class="results5-wrap">

    <div class="topbar">
      <div class="topbar-inner">
        <div class="brand">
          <div class="brand-mark"></div>
          <div class="brand-text">SEQ <small>Swiss Eating Quiz · Dietician Report</small></div>
        </div>
        <div class="topbar-meta">DIETICIAN REPORT · 09.05.2026 · ID 2841-CH</div>
      </div>
    </div>

    <div class="hero">
      <div class="hero-inner">
        <div class="hero-eyebrow">Professional Dietary Assessment</div>
        <h1 class="hero-title">Clinical view of the participant's reported <em>eating habits</em>.</h1>
        <p class="hero-sub">
          Frequency data from the Swiss Eating Quiz, mapped to the Schweizer Lebensmittelpyramide and broken down to the sub-group level. Intended for use during or after a dietetic consultation, alongside the dietician's own clinical assessment.
        </p>
      </div>
    </div>

    <div class="meta-grid">
      <div v-for="cell in metaCells" :key="cell.label" class="meta-cell">
        <div class="meta-label">{{ cell.label }}</div>
        <div class="meta-value">{{ cell.value }}</div>
      </div>
    </div>

    <!-- SECTION 1: SUMMARY -->
    <section class="section">
      <div class="section-inner">
        <div class="section-header">
          <div class="section-num">01 / 07</div>
          <div>
            <h2 class="section-title">Quick <em>summary</em>.</h2>
            <p class="section-lede">
              Headline numbers from the quiz. Use the granular tables in Section 03 to drill down into specific patterns. Use Section 05 for the clinical context fields you complete in session.
            </p>
          </div>
        </div>

        <div class="two-col">
          <div class="card">
            <div class="card-eyebrow">Fig. 1 · Score overview</div>
            <div class="card-title">Composite and per-group scores</div>
            <p class="card-sub">Scores range 0 to 100. Higher means closer to the Swiss recommendation. Sub-100 does not mean unhealthy; it means deviation from the reference.</p>

            <div class="score-block">
              <div v-for="m in summaryMetrics" :key="m.label" class="metric">
                <div class="metric-label">{{ m.label }}</div>
                <div class="metric-value" :class="m.status">
                  {{ m.value }}<span v-if="m.unit" class="unit">{{ m.unit }}</span>
                </div>
                <div class="metric-detail">{{ m.detail }}</div>
              </div>
            </div>
          </div>

          <div class="card">
            <div class="card-eyebrow">Clinical flags</div>
            <div class="card-title">Auto-detected patterns</div>
            <p class="card-sub">Flags raised by frequency thresholds. Severity reflects deviation from recommendation, not health risk per se.</p>

            <ul class="flag-list">
              <li v-for="f in flags" :key="f.label + f.text" :class="f.level">
                <span class="flag-icon">{{ f.label }}</span>
                <span class="flag-text"><strong>{{ f.title }}</strong> {{ f.text }}</span>
                <span class="flag-meta">{{ f.meta }}</span>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </section>

    <!-- SECTION 2: PYRAMID -->
    <section class="section warm">
      <div class="section-inner">
        <div class="section-header">
          <div class="section-num">02 / 07</div>
          <div>
            <h2 class="section-title">Pyramid <em>mapping</em>, with deviation per tier.</h2>
            <p class="section-lede">
              Reported frequency vs. Swiss recommendation per pyramid tier, expressed in standard SGE portions per week. Deviation is the signed gap from the centre of the recommended range.
            </p>
          </div>
        </div>

        <div class="card">
          <div class="card-eyebrow">Tab. 1 · Pyramid tier deviation</div>
          <div class="card-title">Portions per week vs. SGE recommendation</div>

          <table class="data">
            <thead>
              <tr>
                <th>Tier</th>
                <th>Group</th>
                <th class="num">Reported / wk</th>
                <th class="num">SGE recommended / wk</th>
                <th class="num">Deviation</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in pyramidRows" :key="row.group">
                <td>{{ row.tier }}</td>
                <td class="name">{{ row.group }}</td>
                <td class="num">{{ row.reported }}</td>
                <td class="num">{{ row.recommended }}</td>
                <td class="num">{{ row.deviation }}</td>
                <td><span class="pill" :class="row.pill">{{ row.status }}</span></td>
              </tr>
            </tbody>
          </table>
          <p class="card-sub" style="margin-top: 16px; margin-bottom: 0;">
            Composition flag on grains: total quantity is on target but whole-grain share is low. See Section 03 for sub-group breakdown.
          </p>
        </div>
      </div>
    </section>

    <!-- SECTION 3: SUB-GROUP BREAKDOWN -->
    <section class="section">
      <div class="section-inner">
        <div class="section-header">
          <div class="section-num">03 / 07</div>
          <div>
            <h2 class="section-title">Sub-group <em>breakdown</em>.</h2>
            <p class="section-lede">
              Each SEQ food group is composed of multiple sub-categories. Variety within a group is as relevant as total frequency; a participant eating 14 portions of vegetables per week from only 2 sub-categories has different nutritional exposure than one eating from 6.
            </p>
          </div>
        </div>

        <div class="card">
          <div class="card-eyebrow">Tab. 2 · Frequency per sub-category</div>
          <div class="card-title">Reported intake, all 67 SEQ items</div>

          <table class="data">
            <thead>
              <tr>
                <th>Item</th>
                <th class="num">Reported / wk</th>
                <th class="num">Reference</th>
                <th>Note</th>
              </tr>
            </thead>
            <tbody>
              <template v-for="group in subgroupBlocks" :key="group.head">
                <tr class="group-head"><td colspan="4">{{ group.head }}</td></tr>
                <tr v-for="item in group.items" :key="group.head + item.name" class="sub">
                  <td>{{ item.name }}</td>
                  <td class="num">{{ item.reported }}</td>
                  <td class="num">{{ item.reference }}</td>
                  <td>{{ item.note }}</td>
                </tr>
              </template>
            </tbody>
          </table>
        </div>
      </div>
    </section>

    <!-- SECTION 4: NUTRIENT INFERENCES -->
    <section class="section warm">
      <div class="section-inner">
        <div class="section-header">
          <div class="section-num">04 / 07</div>
          <div>
            <h2 class="section-title">Nutrient-level <em>inferences</em>.</h2>
            <p class="section-lede">
              Inferences derived from the reported frequencies. These are dietary-pattern signals, not biochemical measurements. Confirm with lab values where clinically indicated.
            </p>
          </div>
        </div>

        <div class="card">
          <div class="card-eyebrow">Tab. 3 · Predicted intake adequacy</div>
          <div class="card-title">Nutrients of interest based on reported pattern</div>

          <table class="data">
            <thead>
              <tr>
                <th>Nutrient</th>
                <th>Indicator</th>
                <th>Sources reported</th>
                <th>Inference</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="n in nutrients" :key="n.name">
                <td class="name">{{ n.name }}</td>
                <td><span class="pill" :class="n.pill">{{ n.indicator }}</span></td>
                <td>{{ n.sources }}</td>
                <td>{{ n.inference }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </section>

    <!-- SECTION 5: CLINICAL CONTEXT (FILLABLE) -->
    <section class="section">
      <div class="section-inner">
        <div class="section-header">
          <div class="section-num">05 / 07</div>
          <div>
            <h2 class="section-title">Clinical <em>context</em>.</h2>
            <p class="section-lede">
              Information the SEQ does not collect, to be filled in during or after the consultation. These fields are not currently persisted in the SEQ database in v1; the dietician retains the printed or saved copy for their own file.
            </p>
          </div>
        </div>

        <div class="card">
          <div class="card-eyebrow">Anthropometry</div>
          <div class="card-title">Measurements and basic targets</div>

          <div class="form-grid">
            <div class="field"><label>Height (cm)</label><input type="text" placeholder="—"></div>
            <div class="field"><label>Weight (kg)</label><input type="text" placeholder="—"></div>
            <div class="field"><label>BMI (kg / m²)</label><input type="text" placeholder="auto"></div>
            <div class="field"><label>Waist circumference (cm)</label><input type="text" placeholder="—"></div>
            <div class="field"><label>Age (years)</label><input type="text" placeholder="—"></div>
            <div class="field"><label>Sex (M / F / other)</label><input type="text" placeholder="—"></div>
            <div class="field">
              <label>Activity level</label>
              <select>
                <option>— select —</option>
                <option>Sedentary</option>
                <option>Light</option>
                <option>Moderate</option>
                <option>High</option>
                <option>Athlete</option>
              </select>
            </div>
            <div class="field"><label>Target body weight (kg)</label><input type="text" placeholder="—"></div>
          </div>
        </div>

        <div class="card">
          <div class="card-eyebrow">Clinical history</div>
          <div class="card-title">Diagnoses, medications, restrictions</div>

          <div class="form-grid wide-2">
            <div class="field field-wide-2"><label>Presenting concern / consultation reason</label><input type="text" placeholder="—"></div>
            <div class="field field-wide-2"><label>Relevant diagnoses (diabetes, hypertension, IBS, etc.)</label><input type="text" placeholder="—"></div>
            <div class="field field-wide-2"><label>Current medications</label><input type="text" placeholder="—"></div>
            <div class="field field-wide-2"><label>Allergies / intolerances</label><input type="text" placeholder="—"></div>
            <div class="field field-wide-2"><label>Dietary pattern (omnivore, vegetarian, vegan, religious, other)</label><input type="text" placeholder="—"></div>
            <div class="field field-wide-2"><label>Family history of nutrition-related disease</label><input type="text" placeholder="—"></div>
          </div>
        </div>

        <div class="card">
          <div class="card-eyebrow">Recent labs (optional)</div>
          <div class="card-title">If lab values are available, record here for context</div>

          <div class="form-grid">
            <div v-for="lab in labFields" :key="lab" class="field">
              <label>{{ lab }}</label>
              <input type="text" placeholder="—">
            </div>
          </div>
        </div>

        <div class="card">
          <div class="card-eyebrow">Social context</div>
          <div class="card-title">Practical factors that shape food choices</div>

          <div class="form-grid wide-2">
            <div v-for="s in socialFields" :key="s" class="field field-wide-2">
              <label>{{ s }}</label>
              <input type="text" placeholder="—">
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- SECTION 6: PLAN (FILLABLE) -->
    <section class="section warm">
      <div class="section-inner">
        <div class="section-header">
          <div class="section-num">06 / 07</div>
          <div>
            <h2 class="section-title">Action <em>plan</em>.</h2>
            <p class="section-lede">
              Concrete changes agreed with the client during this session. SMART-formatted: specific, measurable, achievable, relevant, time-bound. Limit to three at a time.
            </p>
          </div>
        </div>

        <div class="card">
          <div class="card-eyebrow">Targets agreed</div>
          <div class="card-title">Top three changes for the next 4 weeks</div>

          <div v-for="n in [1, 2, 3]" :key="n" class="plan-row">
            <div class="plan-num">{{ String(n).padStart(2, '0') }}</div>
            <input type="text" placeholder="Specific change agreed with the client">
            <input type="text" placeholder="Target / frequency">
          </div>

          <div class="form-grid wide-4" style="margin-top: 20px;">
            <div class="field field-wide-4">
              <label>Approach (motivational interviewing notes, barriers identified, supports in place)</label>
              <textarea placeholder="—"></textarea>
            </div>
            <div class="field"><label>Follow-up appointment</label><input type="text" placeholder="dd.mm.yyyy"></div>
            <div class="field">
              <label>Re-take SEQ at follow-up?</label>
              <select><option>—</option><option>Yes</option><option>No</option></select>
            </div>
            <div class="field">
              <label>Refer to GP / specialist?</label>
              <select>
                <option>—</option>
                <option>No</option>
                <option>GP</option>
                <option>Endocrinology</option>
                <option>Gastroenterology</option>
                <option>Other</option>
              </select>
            </div>
            <div class="field"><label>Refer specific to</label><input type="text" placeholder="—"></div>
          </div>
        </div>

        <div class="card">
          <div class="card-eyebrow">Recommended educational materials</div>
          <div class="card-title">Resources to share with the client</div>

          <div class="form-grid wide-2">
            <div v-for="r in resourceFields" :key="r" class="field field-wide-2">
              <label>{{ r }}</label>
              <input type="text" placeholder="—">
            </div>
          </div>
        </div>

        <p class="placeholder-note">Placeholder fields. Final structure and storage logic will be confirmed with the Ernährungsteam.</p>
      </div>
    </section>

    <!-- SECTION 7: COMPARISON / NOTES -->
    <section class="section">
      <div class="section-inner">
        <div class="section-header">
          <div class="section-num">07 / 07</div>
          <div>
            <h2 class="section-title">Notes and <em>comparison</em>.</h2>
            <p class="section-lede">
              Free-text observations and longitudinal context. If this is a follow-up consultation, prior SEQ scores are shown alongside the current ones for comparison.
            </p>
          </div>
        </div>

        <div class="card">
          <div class="card-eyebrow">Longitudinal</div>
          <div class="card-title">Score history</div>
          <p class="card-sub">Showing the most recent three SEQ submissions for this participant ID. Use to track progress between consultations.</p>

          <table class="data">
            <thead>
              <tr>
                <th>Date</th>
                <th class="num">Overall</th>
                <th class="num">Veg</th>
                <th class="num">Fruit</th>
                <th class="num">Drinks</th>
                <th class="num">Dairy</th>
                <th class="num">Animal</th>
                <th class="num">Plant</th>
                <th class="num">Grains</th>
                <th>Note</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in history" :key="row.date + row.note">
                <td>{{ row.date }}</td>
                <td class="num"><strong v-if="row.current">{{ row.overall }}</strong><template v-else>{{ row.overall }}</template></td>
                <td class="num">{{ row.veg }}</td>
                <td class="num">{{ row.fruit }}</td>
                <td class="num">{{ row.drinks }}</td>
                <td class="num">{{ row.dairy }}</td>
                <td class="num">{{ row.animal }}</td>
                <td class="num">{{ row.plant }}</td>
                <td class="num">{{ row.grains }}</td>
                <td>{{ row.note }}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <div class="card">
          <div class="card-eyebrow">Dietician notes</div>
          <div class="card-title">Free text</div>

          <div class="form-grid wide-4">
            <div class="field field-wide-4">
              <textarea style="min-height: 140px;" placeholder="Clinical observations, contextual remarks, things to revisit next session..."></textarea>
            </div>
          </div>

          <div class="sig-row">
            <div class="sig-line">Dietician signature and date</div>
            <div class="sig-line">Client signature and date</div>
          </div>
        </div>
      </div>
    </section>

    <div class="footer">
      <div class="footer-inner">
        <div class="footer-brand">
          <div class="brand">
            <div class="brand-mark"></div>
            <div class="brand-text">SEQ <small>Swiss Eating Quiz · Dietician Report</small></div>
          </div>
          <p>
            The SEQ is a research instrument developed at Berner Fachhochschule for measuring how Swiss residents' eating habits compare to the Schweizer Lebensmittelpyramide. The dietician version supports professional consultation but is not a substitute for clinical judgement.
          </p>
        </div>
        <div class="footer-meta">
          Demo report · Mock data<br>
          v0.1 · May 2026<br>
          BFH Departement Gesundheit
        </div>
      </div>
    </div>

  </div>
</template>

<script>
export default {
  name: 'Results5',
  data() {
    return {
      metaCells: [
        { label: 'Participant ID', value: '2841-CH' },
        { label: 'Quiz completed', value: '9 May 2026, 14:23' },
        { label: 'Language · Type', value: 'DE · Self-reported (adult)' },
        { label: 'Dietician', value: '[ to be filled ]' },
      ],
      summaryMetrics: [
        { label: 'Overall score', value: '76', unit: '/ 100', status: '', detail: 'Mid-upper range. Solid foundation with two specific gaps.' },
        { label: 'Response quality', value: 'High', unit: '', status: 'good', detail: '67 / 67 answered · 9 min · no drop-out' },
        { label: 'Strongest group', value: '90', unit: 'Drinks', status: 'good', detail: 'Water-led pattern, no sweetened drinks reported.' },
        { label: 'Weakest group', value: '45', unit: 'Plant proteins', status: 'bad', detail: 'Hülsenfrüchte and Tofu rarely consumed.' },
      ],
      flags: [
        { level: 'high', label: 'High', title: 'Plant protein gap.', text: 'Hülsenfrüchte < 1× / week. Assess iron, B12, zinc status if other constraints present.', meta: 'Q37–44' },
        { level: 'medium', label: 'Medium', title: 'Whole-grain share low.', text: 'White bread / refined grains dominate the grain category.', meta: 'Q45–54' },
        { level: 'medium', label: 'Medium', title: 'Fish intake low.', text: 'Fish < 1× / week, no fatty fish reported. Consider omega-3 sufficiency.', meta: 'Q31–33' },
        { level: 'low', label: 'Low', title: 'Coffee high.', text: '4+ cups daily reported. Cross-check with iron absorption if anaemia history.', meta: 'Q66' },
        { level: 'good', label: 'OK', title: 'Vegetable variety.', text: 'Cross-category diversity reported; no monotony pattern detected.', meta: 'Q1–18' },
      ],
      pyramidRows: [
        { tier: '1 · Base', group: 'Drinks (water, unsweetened tea)', reported: '14 L', recommended: '10.5–14 L', deviation: 'on target', pill: 'good', status: 'OK' },
        { tier: '2', group: 'Vegetables', reported: '18', recommended: '21', deviation: '−3', pill: 'good', status: 'OK' },
        { tier: '2', group: 'Fruit', reported: '12', recommended: '14', deviation: '−2', pill: 'good', status: 'OK' },
        { tier: '3', group: 'Grains, potatoes', reported: '21', recommended: '21', deviation: 'on target', pill: 'warn', status: 'composition' },
        { tier: '3', group: 'Plant proteins', reported: '1.5', recommended: '3–4', deviation: '−2', pill: 'bad', status: 'below' },
        { tier: '4', group: 'Dairy', reported: '14', recommended: '21', deviation: '−7', pill: 'good', status: 'OK' },
        { tier: '4', group: 'Animal proteins (meat, fish, eggs)', reported: '5', recommended: '5–7', deviation: 'low end', pill: 'good', status: 'OK' },
        { tier: '5', group: 'Oils, fats, nuts', reported: '—', recommended: '21–35 g daily', deviation: 'not in SEQ v1', pill: 'flat', status: 'n/a' },
        { tier: '6 · Top', group: 'Sweets, snacks, alcohol', reported: '—', recommended: 'discretionary', deviation: 'not in SEQ v1', pill: 'flat', status: 'n/a' },
      ],
      subgroupBlocks: [
        {
          head: 'Vegetables (Q1–18)',
          items: [
            { name: 'Leafy greens (salad, spinach, chard)', reported: '5', reference: '≥ 7', note: 'under target' },
            { name: 'Tomatoes', reported: '4', reference: '2–4', note: 'on target' },
            { name: 'Root vegetables (carrots, beets)', reported: '3', reference: '2–4', note: 'on target' },
            { name: 'Crucifers (broccoli, cauliflower, cabbage)', reported: '2', reference: '2–4', note: 'low end' },
            { name: 'Peppers, courgette, aubergine', reported: '3', reference: '2–4', note: 'on target' },
            { name: 'Onions, leek, garlic', reported: '7', reference: 'daily', note: 'good' },
            { name: 'Variety score', reported: '6 / 7', reference: '≥ 5 / 7', note: 'good diversity' },
          ],
        },
        {
          head: 'Fruit (Q19–28)',
          items: [
            { name: 'Pome fruit (apple, pear)', reported: '5', reference: '3–7', note: 'on target' },
            { name: 'Berries', reported: '2', reference: '2–4', note: 'on target, seasonal' },
            { name: 'Bananas', reported: '4', reference: '2–7', note: 'on target' },
            { name: 'Citrus', reported: '1', reference: '2–4', note: 'under target' },
            { name: 'Stone fruit (peach, plum)', reported: '0', reference: 'seasonal', note: 'none reported, season-aware' },
            { name: 'Variety score', reported: '4 / 5', reference: '≥ 3 / 5', note: 'good' },
          ],
        },
        {
          head: 'Animal proteins (Q29–36)',
          items: [
            { name: 'Red meat (beef, pork, lamb)', reported: '2', reference: '2–3', note: 'on target' },
            { name: 'Processed meat (Wurst, Salami, Schinken)', reported: '3', reference: '≤ 1', note: 'over target' },
            { name: 'Poultry', reported: '1', reference: '1–2', note: 'on target' },
            { name: 'Fish, lean', reported: '0', reference: '1', note: 'below target' },
            { name: 'Fish, fatty (salmon, mackerel, herring)', reported: '0', reference: '1', note: 'omega-3 gap' },
            { name: 'Eggs', reported: '3', reference: '2–4', note: 'on target' },
          ],
        },
        {
          head: 'Plant proteins (Q37–44)',
          items: [
            { name: 'Lentils', reported: '0.5', reference: '1', note: 'low' },
            { name: 'Chickpeas', reported: '0.5', reference: '1', note: 'low' },
            { name: 'Beans (kidney, white, black)', reported: '0', reference: '1', note: 'none reported' },
            { name: 'Tofu, Tempeh, edamame', reported: '0.5', reference: '1–2', note: 'low' },
            { name: 'Nuts, seeds', reported: '0', reference: 'daily', note: 'not measured directly' },
          ],
        },
        {
          head: 'Grains (Q45–54)',
          items: [
            { name: 'White bread', reported: '7', reference: 'low', note: 'over target' },
            { name: 'Whole-grain bread', reported: '2', reference: '≥ 7', note: 'under target' },
            { name: 'Pasta (refined)', reported: '4', reference: 'low', note: 'over target' },
            { name: 'Whole-grain pasta', reported: '0', reference: '2–3', note: 'none reported' },
            { name: 'Rice, white', reported: '2', reference: 'moderate', note: 'on target' },
            { name: 'Rice, brown / wild', reported: '0', reference: '1', note: 'none reported' },
            { name: 'Potatoes', reported: '3', reference: '2–3', note: 'on target' },
            { name: 'Cereal, sugar-added', reported: '3', reference: 'low', note: 'over target' },
            { name: 'Cereal, plain (Müesli, Haferflocken)', reported: '1', reference: 'moderate', note: 'under target' },
            { name: 'Whole-grain share', reported: '≈ 18 %', reference: '≥ 50 %', note: 'low' },
          ],
        },
        {
          head: 'Dairy (Q55–64)',
          items: [
            { name: 'Milk', reported: '3', reference: 'moderate', note: 'on target' },
            { name: 'Yoghurt, plain', reported: '3', reference: 'moderate', note: 'on target' },
            { name: 'Yoghurt, fruit (sugar-added)', reported: '2', reference: 'low', note: 'over target' },
            { name: 'Quark, fresh cheese', reported: '2', reference: 'moderate', note: 'on target' },
            { name: 'Hard cheese', reported: '3', reference: 'moderate', note: 'on target' },
            { name: 'Cream-based products', reported: '1', reference: 'low', note: 'on target' },
          ],
        },
        {
          head: 'Drinks (Q65–67)',
          items: [
            { name: 'Water', reported: '2.0 L / day', reference: '1.5–2 L', note: 'on target' },
            { name: 'Coffee', reported: '4 cups / day', reference: '≤ 3', note: 'over target' },
            { name: 'Tea, unsweetened', reported: '1 cup / day', reference: 'any', note: 'OK' },
            { name: 'Sweetened drinks', reported: '0', reference: 'avoid', note: 'OK' },
          ],
        },
      ],
      nutrients: [
        { name: 'Protein, total', pill: 'good', indicator: 'adequate', sources: 'Meat, dairy, eggs (mixed)', inference: 'Likely adequate. Composition skewed to animal sources.' },
        { name: 'Omega-3 (EPA / DHA)', pill: 'bad', indicator: 'low', sources: 'No fatty fish reported', inference: 'Likely below reference. Consider weekly fatty fish or supplementation.' },
        { name: 'Iron', pill: 'warn', indicator: 'attention', sources: 'Red meat (2×), low Hülsenfrüchte, high coffee', inference: 'Coffee may impair non-heme iron absorption. Check ferritin if symptomatic.' },
        { name: 'Calcium', pill: 'good', indicator: 'adequate', sources: 'Dairy (14× / wk)', inference: 'Reference likely met from dairy alone.' },
        { name: 'Vitamin B12', pill: 'good', indicator: 'adequate', sources: 'Meat, dairy, eggs', inference: 'Adequate from animal sources.' },
        { name: 'Folate', pill: 'warn', indicator: 'attention', sources: 'Vegetables OK, low Hülsenfrüchte', inference: 'Hülsenfrüchte are a major source; gap warrants attention especially if planning pregnancy.' },
        { name: 'Fibre', pill: 'warn', indicator: 'attention', sources: 'Vegetables and fruit OK, low whole grains, low Hülsenfrüchte', inference: 'Likely below 30 g / day. Whole-grain switch and Hülsenfrüchte addition would close gap.' },
        { name: 'Salt (Na)', pill: 'warn', indicator: 'attention', sources: 'Processed meat 3× / wk, hard cheese 3× / wk', inference: 'Likely above 5 g / day reference. Worth quantifying if hypertension history.' },
        { name: 'Saturated fat', pill: 'warn', indicator: 'attention', sources: 'Hard cheese, processed meat, dairy', inference: 'Composition suggests upper end of reference.' },
        { name: 'Added sugar', pill: 'warn', indicator: 'attention', sources: 'Sugar-added cereal 3×, fruit yoghurt 2×', inference: 'Discretionary intake from these alone; total not measured in SEQ v1.' },
      ],
      labFields: [
        'HbA1c (%)',
        'Glucose, fasting (mmol / L)',
        'LDL (mmol / L)',
        'HDL (mmol / L)',
        'Triglycerides (mmol / L)',
        'Ferritin (ng / mL)',
        'Vitamin D (nmol / L)',
        'Vitamin B12 (pmol / L)',
        'Blood pressure (mmHg)',
        'Other',
      ],
      socialFields: [
        'Household composition (lives alone, with family, children, etc.)',
        'Who prepares meals?',
        'Work pattern (shifts, travel, home office)',
        'Budget / access constraints',
        'Cooking confidence / skills',
        'Readiness to change (1 to 10)',
      ],
      resourceFields: [
        'SGE materials (Lebensmittelpyramide, Tellermodell, etc.)',
        'Recipe suggestions (Hülsenfrüchte, whole-grain swaps, fish)',
        'Food diary / app recommendation',
        'Other',
      ],
      history: [
        { date: '09.05.2026', overall: '76', veg: '88', fruit: '82', drinks: '90', dairy: '78', animal: '70', plant: '45', grains: '65', note: 'Current', current: true },
        { date: '15.02.2026', overall: '72', veg: '85', fruit: '80', drinks: '85', dairy: '75', animal: '68', plant: '40', grains: '62', note: 'Pre-intervention', current: false },
        { date: '—', overall: '—', veg: '—', fruit: '—', drinks: '—', dairy: '—', animal: '—', plant: '—', grains: '—', note: '—', current: false },
      ],
    };
  },
};
</script>

<style scoped>
.results5-wrap {
  --ink: #1A1A1A;
  --ink-soft: #2D2D2D;
  --ink-mute: #6B6B6B;
  --ink-dim: #9C9C9C;
  --rule: #DDDDDD;
  --rule-strong: #1A1A1A;
  --paper: #FAFAF7;
  --card: #FFFFFF;
  --warm: #F2EDE4;
  --good: #2D8659;
  --good-bg: #B8D8C5;
  --warn: #E8A33D;
  --warn-bg: #F5D9A8;
  --bad: #C8392E;
  --bad-bg: #ECB8B3;
  --neutral: #4A6FA5;
  --accent-rule: #DA291C;
  --mono: 'JetBrains Mono', 'SF Mono', Consolas, monospace;

  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  font-size: 14px;
  line-height: 1.55;
  color: var(--ink);
  background: var(--paper);
  min-height: 100%;
}
.results5-wrap *,
.results5-wrap *::before,
.results5-wrap *::after {
  box-sizing: border-box;
}

/* TOP BAR */
.topbar {
  background: var(--ink);
  color: white;
  font-family: var(--mono);
  font-size: 11px;
  letter-spacing: 0.08em;
  padding: 12px 32px;
}
.topbar-inner {
  display: flex;
  justify-content: space-between;
  align-items: center;
  max-width: 1100px;
  margin: 0 auto;
}
.brand {
  display: flex;
  align-items: center;
  gap: 12px;
}
.brand-mark {
  width: 18px;
  height: 18px;
  background: var(--accent-rule);
}
.brand-text {
  font-family: 'Inter', sans-serif;
  font-weight: 600;
  font-size: 14px;
  letter-spacing: 0.02em;
}
.brand-text small {
  display: block;
  font-weight: 400;
  font-size: 10px;
  letter-spacing: 0.04em;
  color: var(--ink-dim);
  margin-top: -2px;
}
.topbar-meta {
  font-size: 11px;
}

/* HEADER */
.hero {
  background: var(--card);
  padding: 32px;
  border-bottom: 1px solid var(--rule);
}
.hero-inner {
  max-width: 1100px;
  margin: 0 auto;
}
.hero-eyebrow {
  font-family: var(--mono);
  font-size: 11px;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: var(--bad);
  margin-bottom: 12px;
}
.hero-title {
  font-size: 36px;
  font-weight: 600;
  line-height: 1.15;
  margin: 0 0 12px 0;
  letter-spacing: -0.01em;
}
.hero-title em {
  font-style: italic;
  color: var(--bad);
}
.hero-sub {
  color: var(--ink-mute);
  font-size: 15px;
  max-width: 680px;
  margin: 0 0 24px 0;
}

/* META GRID */
.meta-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 0;
  border-top: 1px solid var(--rule);
  border-bottom: 1px solid var(--rule);
  background: var(--warm);
}
.meta-cell {
  padding: 14px 20px;
  border-right: 1px solid var(--rule);
  font-size: 12px;
}
.meta-cell:last-child { border-right: none; }
.meta-label {
  font-family: var(--mono);
  font-size: 10px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--ink-mute);
  margin-bottom: 4px;
}
.meta-value {
  font-weight: 500;
  color: var(--ink);
}

/* SECTION */
.section {
  padding: 40px 32px;
  border-bottom: 1px solid var(--rule);
}
.section.warm { background: var(--warm); }
.section-inner {
  max-width: 1100px;
  margin: 0 auto;
}
.section-header {
  display: grid;
  grid-template-columns: 80px 1fr;
  gap: 24px;
  margin-bottom: 28px;
}
.section-num {
  font-family: var(--mono);
  font-size: 11px;
  letter-spacing: 0.12em;
  color: var(--ink-dim);
  padding-top: 8px;
  border-top: 1px solid var(--ink);
}
.section-title {
  font-size: 24px;
  font-weight: 600;
  margin: 0 0 8px 0;
  line-height: 1.2;
}
.section-title em {
  font-style: italic;
  color: var(--bad);
}
.section-lede {
  color: var(--ink-mute);
  font-size: 14px;
  max-width: 780px;
}

/* CARD */
.card {
  background: var(--card);
  border: 1px solid var(--rule);
  padding: 24px;
  margin-bottom: 16px;
}
.card-eyebrow {
  font-family: var(--mono);
  font-size: 10px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--ink-dim);
  margin-bottom: 4px;
}
.card-title {
  font-size: 18px;
  font-weight: 600;
  margin-bottom: 4px;
}
.card-sub {
  color: var(--ink-mute);
  font-size: 13px;
  margin-bottom: 20px;
}

/* TWO COLUMNS */
.two-col {
  display: grid;
  grid-template-columns: 1.4fr 1fr;
  gap: 16px;
}

/* TABLES */
table.data {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
}
table.data th {
  text-align: left;
  font-family: var(--mono);
  font-size: 10px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--ink-mute);
  padding: 8px 12px;
  border-bottom: 1px solid var(--ink);
  font-weight: 500;
}
table.data td {
  padding: 10px 12px;
  border-bottom: 1px solid var(--rule);
  vertical-align: top;
}
table.data tr:last-child td { border-bottom: 1px solid var(--ink); }
table.data .num {
  font-family: var(--mono);
  text-align: right;
}
table.data .name { font-weight: 500; }
table.data tr.group-head td {
  background: var(--warm);
  font-weight: 600;
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}
table.data .sub td:first-child { padding-left: 28px; color: var(--ink-mute); }

/* STATUS PILLS */
.pill {
  display: inline-block;
  font-family: var(--mono);
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.04em;
  padding: 2px 8px;
  border-radius: 2px;
}
.pill.good { background: var(--good-bg); color: var(--good); }
.pill.warn { background: var(--warn-bg); color: var(--warn); }
.pill.bad { background: var(--bad-bg); color: var(--bad); }
.pill.flat { background: var(--rule); color: var(--ink-mute); }

/* SCORE BLOCK */
.score-block {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  margin-bottom: 16px;
}
.metric {
  border: 1px solid var(--rule);
  padding: 14px 16px;
  background: var(--card);
}
.metric-label {
  font-family: var(--mono);
  font-size: 10px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--ink-mute);
  margin-bottom: 2px;
}
.metric-value {
  font-size: 22px;
  font-weight: 600;
  font-family: 'Inter', sans-serif;
}
.metric-value .unit {
  font-size: 13px;
  font-weight: 400;
  color: var(--ink-mute);
  margin-left: 4px;
}
.metric-detail {
  font-size: 12px;
  color: var(--ink-mute);
  margin-top: 2px;
}
.metric-value.good { color: var(--good); }
.metric-value.warn { color: var(--warn); }
.metric-value.bad { color: var(--bad); }

/* FLAGS */
.flag-list {
  list-style: none;
  margin: 0;
  padding: 0;
}
.flag-list li {
  padding: 12px 14px;
  border-left: 3px solid var(--rule);
  background: var(--card);
  margin-bottom: 8px;
  font-size: 13px;
  display: grid;
  grid-template-columns: auto 1fr auto;
  gap: 12px;
  align-items: center;
}
.flag-list li.high { border-left-color: var(--bad); }
.flag-list li.medium { border-left-color: var(--warn); }
.flag-list li.low { border-left-color: var(--neutral); }
.flag-list li.good { border-left-color: var(--good); }
.flag-icon {
  font-family: var(--mono);
  font-size: 10px;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  font-weight: 600;
}
.flag-list li.high .flag-icon { color: var(--bad); }
.flag-list li.medium .flag-icon { color: var(--warn); }
.flag-list li.low .flag-icon { color: var(--neutral); }
.flag-list li.good .flag-icon { color: var(--good); }
.flag-text strong { font-weight: 600; }
.flag-meta {
  font-family: var(--mono);
  font-size: 11px;
  color: var(--ink-mute);
}

/* CLINICAL FORM */
.form-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 12px 16px;
  margin-bottom: 16px;
}
.form-grid.wide-2 .field-wide-2 { grid-column: span 2; }
.form-grid.wide-4 .field-wide-4 { grid-column: span 4; }
.field {
  display: flex;
  flex-direction: column;
}
.field-wide-2 { grid-column: span 2; }
.field-wide-4 { grid-column: span 4; }
.field label {
  font-family: var(--mono);
  font-size: 10px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--ink-mute);
  margin-bottom: 4px;
}
.field input, .field textarea, .field select {
  font-family: 'Inter', sans-serif;
  font-size: 13px;
  color: var(--ink);
  padding: 8px 10px;
  border: 1px solid var(--rule);
  background: var(--card);
  border-radius: 0;
}
.field input:focus, .field textarea:focus, .field select:focus {
  outline: 1px solid var(--bad);
  outline-offset: -1px;
}
.field textarea { min-height: 60px; font-family: 'Inter', sans-serif; resize: vertical; }

/* ACTION PLAN */
.plan-row {
  display: grid;
  grid-template-columns: 24px 1fr 140px;
  gap: 12px;
  padding: 10px 0;
  border-bottom: 1px solid var(--rule);
  align-items: start;
}
.plan-row:last-child { border-bottom: none; }
.plan-num {
  font-family: var(--mono);
  font-weight: 600;
  font-size: 12px;
  color: var(--bad);
  padding-top: 8px;
}
.plan-row input[type="text"] {
  width: 100%;
  font-family: 'Inter', sans-serif;
  font-size: 13px;
  padding: 8px 10px;
  border: 1px solid var(--rule);
}

/* FOOTER */
.footer {
  background: var(--ink);
  color: white;
  padding: 32px;
  font-size: 12px;
}
.footer-inner {
  max-width: 1100px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 24px;
}
.footer p {
  margin: 8px 0 0 0;
  color: var(--ink-dim);
  max-width: 600px;
}
.footer-meta {
  font-family: var(--mono);
  font-size: 11px;
  color: var(--ink-dim);
  text-align: right;
}
.placeholder-note {
  font-family: var(--mono);
  font-size: 10px;
  color: var(--ink-dim);
  text-align: center;
  margin-top: 20px;
}
.sig-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 32px;
  margin-top: 24px;
}
.sig-line {
  border-top: 1px solid var(--ink);
  padding-top: 6px;
  font-size: 11px;
  color: var(--ink-mute);
  font-family: var(--mono);
  text-transform: uppercase;
  letter-spacing: 0.06em;
}

/* PRINT */
@media print {
  .results5-wrap { background: white; }
  .topbar { background: white; color: black; }
  .hero, .card, .metric, .field input, .field textarea { background: white; }
  .section { padding: 24px; }
  .no-print { display: none; }
  .page-break { page-break-before: always; }
  table.data { font-size: 11px; }
  .meta-grid { background: white; }
}
</style>
